import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { TableData } from "@/App/TimeEntries/Table";
import { getTimeEntriesData } from "@/Api/timeEntries";
import { useAuth } from "@/hooks/auth";
import {
  TimeEntriesExtraFilters,
  defaultTimeEntryFilters,
} from "@/App/TimeEntries/Filters";
import { UnityFilter, useUnityFilter } from "@/components/UnityFilter";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Fingerprint } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export type TableDataProps = {
  id: number;
  uuid: string;
  employee_id: number;
  time_entry_type_id: number;
  origin: number;
  clocked_at: string;
  latitude: number;
  longitude: number;
  address: string | null;
  within_geofence: boolean | null;
  is_late: boolean | null;
  photo_path: string | null;
  device: string | null;
  app_version: string | null;
  ip_address: string | null;
  justification: string | null;
  created_at: string;
  employee: {
    id: number;
    user: {
      id: number;
      name: string;
      photo: string | null;
    };
    position: {
      id: number;
      name: string;
    } | null;
    sector: {
      id: number;
      name: string;
    } | null;
  };
  created_by: {
    id: number;
    name: string;
  } | null;
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const user = useAuth();
  const unityFilter = useUnityFilter({ initialExtra: defaultTimeEntryFilters() });
  const unityId =
    unityFilter.param ?? (user?.user?.unity?.[0]?.id ? String(user.user.unity[0].id) : "");
  useEffect(() => {
    if (!unityFilter.ready) return;
    (async () => {
      setState("loading");
      const { data } = await getTimeEntriesData(1, unityId, unityFilter.nameParam, unityFilter.appliedExtra);
      setListData(data?.response);
      setState("production");
    })();
  }, [unityFilter.ready, unityId, unityFilter.nameParam, unityFilter.appliedExtra]);
  const { hasPermission } = usePermissions();

  const pageActions: TypePageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        setState("loading");

        const { data } = await getTimeEntriesData(
          listData?.current_page + 1,
          unityId, unityFilter.nameParam
        );
        setListData(data.response);
        setState("production");
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        setState("loading");

        const { data } = await getTimeEntriesData(
          listData?.current_page - 1,
          unityId, unityFilter.nameParam
        );
        setListData(data.response);
        setState("production");
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        setState("loading");

        const { data } = await getTimeEntriesData(listData?.last_page, unityId, unityFilter.nameParam, unityFilter.appliedExtra);
        setListData(data?.response);
        setState("production");
      }
      return;
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-time-entries-list")}>
        <PageHeader
          icon={Fingerprint}
          title="Registros de Ponto"
          description="Consulte as batidas de ponto dos colaboradores"
        />
        <div className="flex flex-col gap-3">
          <UnityFilter filter={unityFilter} namePlaceholder="Buscar por nome">
            <TimeEntriesExtraFilters filter={unityFilter} />
          </UnityFilter>
          {state === "loading" ? (
            <CreateLoaderTable cols={4} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              updateFront={setListData}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
